import { useState, useEffect, useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, ArrowUpDown, Package, DollarSign, ArrowDownToLine, ShoppingBag, AlertTriangle, RefreshCw, TrendingUp, Loader2, BarChart3, Store, FileSpreadsheet, Filter, LayoutDashboard, TableIcon } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import axios from "axios";
import LocalExportReport from "@/components/LocalExportReport";
import LocalReportsExplorer from "@/components/LocalReportsExplorer";
import LocalSheetsConfig from "@/components/LocalSheetsConfig";
import TCBadge from "@/components/TCBadge";

import { API_URL as API } from "@/lib/api";
const formatBs = (v) => new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0) + ' Bs';
const PIE_COLORS = ['#0284c7', '#0d9488', '#d97706', '#7c3aed', '#e11d48', '#64748b', '#059669', '#dc2626', '#6366f1', '#0891b2', '#ca8a04', '#be185d'];

const MES_MAP = { ene: '01', feb: '02', mar: '03', abr: '04', may: '05', jun: '06', jul: '07', ago: '08', sep: '09', oct: '10', nov: '11', dic: '12' };
const parseFechaToYYYYMM = (f) => {
  if (!f) return null;
  // Handle YYYY-MM-DD
  if (/^\d{4}-\d{2}/.test(f)) return f.substring(0, 7);
  // Handle D-MMM-YY (e.g., "10-mar-26")
  const parts = f.split('-');
  if (parts.length === 3) {
    const mesKey = (parts[1] || '').toLowerCase().substring(0, 3);
    const mesNum = MES_MAP[mesKey];
    if (mesNum) {
      const yr = parts[2].length === 2 ? '20' + parts[2] : parts[2];
      return `${yr}-${mesNum}`;
    }
  }
  return null;
};
const MES_LABELS = { '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr', '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic' };
const formatMesLabel = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split('-');
  return `${MES_LABELS[m] || m} ${y}`;
};

const ALL_DATA_TABS = [
  { key: "inventario", label: "Inventario", icon: Package, roles: ["admin", "supervisor", "viewer"] },
  { key: "ingresos", label: "Ingresos QMS", icon: ArrowDownToLine, roles: ["admin", "supervisor"] },
  { key: "ventas", label: "Ventas MS", icon: ShoppingBag, roles: ["admin"] },
];

const MONETARY_KEYS = ['pmenor', 'pmayor', 'precio_unit', 'subtotal'];
const INV_COLS_ALL = [
  { key: "codigo", label: "Codigo" }, { key: "descripcion", label: "Descripcion" },
  { key: "familia", label: "Familia" }, { key: "subfamilia", label: "SubFamilia" },
  { key: "unidad", label: "Unidad" }, { key: "cantidad_inicial", label: "Cant.Ini" },
  { key: "ingreso", label: "Ingreso" }, { key: "salida", label: "Salida" },
  { key: "stock", label: "Stock" }, { key: "pmenor", label: "PMenor" }, { key: "pmayor", label: "PMayor" },
  { key: "lote", label: "Lote" },
];
const ING_COLS_ALL = [
  { key: "fecha", label: "Fecha" }, { key: "codigo", label: "Codigo" },
  { key: "descripcion", label: "Descripcion" }, { key: "familia", label: "Familia" },
  { key: "subfamilia", label: "SubFamilia" }, { key: "cantidad", label: "Cant." },
  { key: "unidad", label: "Unidad" }, { key: "pmenor", label: "PMenor" },
  { key: "pmayor", label: "PMayor" }, { key: "tipo_entrega", label: "Tipo" },
];
const VEN_COLS_ALL = [
  { key: "fecha", label: "Fecha" }, { key: "codigo", label: "Codigo" },
  { key: "descripcion", label: "Descripcion" }, { key: "familia", label: "Familia" },
  { key: "subfamilia", label: "SubFamilia" }, { key: "unidad", label: "Unidad" },
  { key: "cantidad", label: "Cant." }, { key: "tipo_precio", label: "P1/P2" },
  { key: "precio_unit", label: "P.Unit" }, { key: "subtotal", label: "SubTotal" },
];

const LocalStorePage = ({ localId, localName, accentClass, accentBg, userRole = "admin", globalTC }) => {
  const [mainView, setMainView] = useState("datos");
  const [activeTab, setActiveTab] = useState("inventario");
  const [dashboard, setDashboard] = useState(null);
  const [data, setData] = useState({ inventario: [], ingresos: [], ventas: [] });
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tipoCambio, setTipoCambio] = useState(9.4);
  const [showFilters, setShowFilters] = useState(false);

  // Sync local TC with global
  useEffect(() => {
    if (globalTC !== null && globalTC !== undefined) setTipoCambio(globalTC);
  }, [globalTC]);
  const [exporting, setExporting] = useState(false);
  const [showReportsExplorer, setShowReportsExplorer] = useState(false);

  // Dashboard family filters (independent per chart)
  const [famTopFam, setFamTopFam] = useState("__all__");
  const [famStock, setFamStock] = useState("__all__");
  const [famVentasFam, setFamVentasFam] = useState("__all__");
  const [famVentasMes, setFamVentasMes] = useState("__all__");
  const [famIngresosMes, setFamIngresosMes] = useState("__all__");

  // Filters
  const [filterFamilia, setFilterFamilia] = useState("__all__");
  const [filterSubFamilia, setFilterSubFamilia] = useState("__all__");
  const [filterStockMin, setFilterStockMin] = useState(0);
  const [filterStockMax, setFilterStockMax] = useState(999999);
  const [filterLoteMin, setFilterLoteMin] = useState("__all__");
  const [filterLoteMax, setFilterLoteMax] = useState("__all__");

  const tc = tipoCambio || 1;
  const formatUSD = useCallback((v) => '$ ' + new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format((v || 0) / tc), [tc]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, invRes, ingRes, venRes] = await Promise.all([
        axios.get(`${API}/api/local/${localId}/dashboard`),
        axios.get(`${API}/api/local/${localId}/inventario`),
        axios.get(`${API}/api/local/${localId}/ingresos`),
        axios.get(`${API}/api/local/${localId}/ventas`),
      ]);
      setDashboard(dashRes.data);
      setData({ inventario: invRes.data.data || [], ingresos: ingRes.data.data || [], ventas: venRes.data.data || [] });
    } catch (e) { console.error("Error:", e); }
    finally { setLoading(false); }
  }, [localId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try { await axios.post(`${API}/api/local/${localId}/sync-all`); await fetchData(); }
    catch (e) { console.error("Sync error:", e); }
    finally { setSyncing(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await axios.get(`${API}/api/local/${localId}/export/${activeTab}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${localId}_${activeTab}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("Export error:", e); }
    finally { setExporting(false); }
  };

  // Unique filter options
  const familias = useMemo(() => [...new Set(data[activeTab]?.map(r => r.familia).filter(Boolean))].sort(), [data, activeTab]);
  const allFamilias = useMemo(() => [...new Set(data.inventario.map(r => r.familia).filter(Boolean))].sort(), [data.inventario]);
  const ventasFamilias = useMemo(() => [...new Set(data.ventas.map(r => r.familia).filter(Boolean))].sort(), [data.ventas]);
  const ingresosFamilias = useMemo(() => [...new Set(data.ingresos.map(r => r.familia).filter(Boolean))].sort(), [data.ingresos]);
  const lotes = useMemo(() => [...new Set(data.inventario?.map(r => r.lote).filter(Boolean))].sort(), [data.inventario]);
  const subfamilias = useMemo(() => {
    const items = filterFamilia === "__all__" ? data[activeTab] : data[activeTab]?.filter(r => r.familia === filterFamilia);
    return [...new Set(items?.map(r => r.subfamilia).filter(Boolean))].sort();
  }, [data, activeTab, filterFamilia]);

  // Charts data - filtered by family selectors
  const ff = (arr, fam) => fam === "__all__" ? arr : arr.filter(r => r.familia === fam);

  const topFamilias = useMemo(() => {
    const items = ff(data.inventario, famTopFam);
    const map = {};
    items.forEach(p => {
      const f = famTopFam !== "__all__" ? (p.subfamilia || 'Sin SubFamilia') : (p.familia || 'Sin Familia');
      if (!map[f]) map[f] = { familia: f, valor: 0, items: 0, stock: 0 };
      map[f].valor += (p.stock || 0) * (p.pmenor || 0);
      map[f].items += 1;
      map[f].stock += p.stock || 0;
    });
    return Object.values(map).sort((a, b) => b.valor - a.valor).slice(0, 10);
  }, [data.inventario, famTopFam]);

  const stockDistribution = useMemo(() => {
    const items = ff(data.inventario, famStock);
    let sinStock = 0, bajo = 0, normal = 0, alto = 0;
    items.forEach(p => {
      const s = p.stock || 0;
      if (s === 0) sinStock++;
      else if (s < 10) bajo++;
      else if (s < 50) normal++;
      else alto++;
    });
    return [
      { name: "Sin Stock", value: sinStock, fill: "#dc2626" },
      { name: "Bajo (<10)", value: bajo, fill: "#f59e0b" },
      { name: "Normal (10-49)", value: normal, fill: "#0284c7" },
      { name: "Alto (50+)", value: alto, fill: "#059669" },
    ].filter(d => d.value > 0);
  }, [data.inventario, famStock]);

  const ventasPorMes = useMemo(() => {
    const items = ff(data.ventas, famVentasMes);
    const map = {};
    items.forEach(v => {
      const mes = parseFechaToYYYYMM(v.fecha);
      if (!mes) return;
      if (!map[mes]) map[mes] = { mes, label: formatMesLabel(mes), total: 0, cantidad: 0 };
      map[mes].total += v.subtotal || 0;
      map[mes].cantidad += v.cantidad || 0;
    });
    return Object.values(map).sort((a, b) => a.mes.localeCompare(b.mes));
  }, [data.ventas, famVentasMes]);

  const ventasPorFamilia = useMemo(() => {
    const items = ff(data.ventas, famVentasFam);
    const map = {};
    items.forEach(v => {
      const f = famVentasFam !== "__all__" ? (v.subfamilia || 'Sin SubFamilia') : (v.familia || 'Sin Familia');
      if (!map[f]) map[f] = { familia: f, total: 0, cantidad: 0 };
      map[f].total += v.subtotal || 0;
      map[f].cantidad += v.cantidad || 0;
    });
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [data.ventas, famVentasFam]);

  const ingresosPorMes = useMemo(() => {
    const items = ff(data.ingresos, famIngresosMes);
    const map = {};
    items.forEach(v => {
      const mes = parseFechaToYYYYMM(v.fecha);
      if (!mes) return;
      if (!map[mes]) map[mes] = { mes, label: formatMesLabel(mes), cantidad: 0, valor: 0 };
      map[mes].cantidad += v.cantidad || 0;
      map[mes].valor += (v.cantidad || 0) * (v.pmayor || 0);
    });
    return Object.values(map).sort((a, b) => a.mes.localeCompare(b.mes));
  }, [data.ingresos, famIngresosMes]);

  // Role-aware tabs and columns
  const dataTabs = useMemo(() => ALL_DATA_TABS.filter(t => t.roles.includes(userRole)), [userRole]);
  const filterCols = useCallback((allCols) => 
    userRole === "viewer" ? allCols.filter(c => !MONETARY_KEYS.includes(c.key)) : allCols
  , [userRole]);

  // Table logic
  const currentData = data[activeTab] || [];
  const cols = activeTab === "inventario" ? filterCols(INV_COLS_ALL) : activeTab === "ingresos" ? filterCols(ING_COLS_ALL) : filterCols(VEN_COLS_ALL);

  const filtered = useMemo(() => {
    let result = currentData;
    if (search) {
      const t = search.toLowerCase();
      result = result.filter(r => (r.codigo || '').toLowerCase().includes(t) || (r.descripcion || '').toLowerCase().includes(t));
    }
    if (filterFamilia !== "__all__") result = result.filter(r => r.familia === filterFamilia);
    if (filterSubFamilia !== "__all__") result = result.filter(r => r.subfamilia === filterSubFamilia);
    if (activeTab === "inventario") {
      result = result.filter(r => (r.stock || 0) >= filterStockMin && (r.stock || 0) <= filterStockMax);
      if (filterLoteMin !== "" && filterLoteMin !== "__all__") {
        const min = parseInt(filterLoteMin, 10);
        if (!isNaN(min)) result = result.filter(r => parseInt(r.lote, 10) >= min);
      }
      if (filterLoteMax !== "" && filterLoteMax !== "__all__") {
        const max = parseInt(filterLoteMax, 10);
        if (!isNaN(max)) result = result.filter(r => parseInt(r.lote, 10) <= max);
      }
    }
    return result;
  }, [currentData, search, filterFamilia, filterSubFamilia, filterStockMin, filterStockMax, filterLoteMin, filterLoteMax, activeTab]);

  const sorted = useMemo(() => {
    if (!sortConfig.key) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sortConfig.key], bv = b[sortConfig.key];
      if (typeof av === 'string') return sortConfig.direction === 'asc' ? (av || '').localeCompare(bv || '') : (bv || '').localeCompare(av || '');
      return sortConfig.direction === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
  }, [filtered, sortConfig]);

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));

  const inv = dashboard?.inventario || {};
  const ing = dashboard?.ingresos || {};
  const ven = dashboard?.ventas || {};

  const renderCell = (row, col) => {
    const v = row[col.key];
    if (col.key === 'descripcion') return <span className="block max-w-[260px] leading-snug line-clamp-2">{v}</span>;
    if (col.key === 'stock') {
      const s = v || 0;
      return <span className={`font-bold ${s === 0 ? 'text-red-600' : s < 10 ? 'text-amber-600' : 'text-emerald-700'}`}>{s}</span>;
    }
    if (['pmenor','pmayor'].includes(col.key) && row.en_liquidacion) {
      const liq = col.key === 'pmenor' ? row.pmenor_liq : row.pmayor_liq;
      return (
        <span className="whitespace-nowrap" data-testid={`local-precio-liq-${col.key}`}>
          <span className="line-through text-muted-foreground text-[10px]">{formatBs(v)}</span>{' '}
          <span className="font-bold text-emerald-600 dark:text-emerald-400">{formatBs(liq)}</span>
          <Badge className="ml-1 bg-violet-600/15 text-violet-700 border-violet-400 text-[9px] border dark:text-violet-400">-{row.descuento_pct}%</Badge>
        </span>
      );
    }
    if (['pmenor','pmayor','precio_unit','subtotal'].includes(col.key)) return <span className="whitespace-nowrap">{formatBs(v)}</span>;
    if (col.key === 'fecha') return <span className="text-[#FF9900] font-medium whitespace-nowrap">{v || ''}</span>;
    if (col.key === 'tipo_precio') return v ? <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] border">{v}</Badge> : '';
    if (col.key === 'tipo_entrega') return v ? <Badge variant="outline" className="text-[10px]">{v}</Badge> : '';
    return v ?? '';
  };

  const resetFilters = () => { setFilterFamilia("__all__"); setFilterSubFamilia("__all__"); setFilterStockMin(0); setFilterStockMax(999999); setFilterLoteMin("__all__"); setFilterLoteMax("__all__"); };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <span className="ml-3 text-muted-foreground">Cargando datos de {localName}...</span>
    </div>
  );

  return (
    <div className="space-y-5" data-testid={`local-page-${localId}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${accentBg}`}>
            <Store className={`h-6 w-6 ${accentClass}`} />
          </div>
          <div>
            <h1 className="heading-font text-xl font-bold text-foreground" data-testid="local-title">{localName}</h1>
            <p className="body-font text-xs text-muted-foreground">Datos del sistema Mastersoft</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TCBadge tc={tipoCambio} />
          {(userRole === "admin" || userRole === "supervisor") && (
            <LocalSheetsConfig localId={localId} localName={localName} onSyncComplete={fetchData} />
          )}
          {(userRole === "admin" || userRole === "supervisor") && (
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="local-sync-btn" className="text-xs gap-1.5 h-7">
            {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            {syncing ? 'Sincronizando...' : 'Sincronizar'}
          </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className={`grid grid-cols-2 md:grid-cols-4 ${userRole === "admin" ? "lg:grid-cols-7" : "lg:grid-cols-4"} gap-3`} data-testid="local-kpis">
        <Card className="p-3 border border-border shadow-sm">
          <div className="flex items-center gap-2 mb-1"><Package className="h-4 w-4 text-muted-foreground" /><span className="text-[10px] text-muted-foreground uppercase tracking-wide">Productos</span></div>
          <p className="heading-font text-lg font-bold">{(inv.total_productos || 0).toLocaleString()}</p>
        </Card>
        <Card className="p-3 border border-border shadow-sm">
          <div className="flex items-center gap-2 mb-1"><Package className="h-4 w-4 text-muted-foreground" /><span className="text-[10px] text-muted-foreground uppercase tracking-wide">Uds. Stock</span></div>
          <p className="heading-font text-lg font-bold">{(inv.total_unidades || 0).toLocaleString()}</p>
        </Card>
        {userRole === "admin" && (
        <Card className="p-3 border shadow-sm border-emerald-200 bg-emerald-50/50">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="h-4 w-4 text-emerald-600" /><span className="text-[10px] text-emerald-700 uppercase tracking-wide">Valor Inv.</span></div>
          <p className="heading-font text-sm font-bold text-emerald-800">{formatBs(inv.valor_pmenor)}</p>
          <p className="mono-font text-[10px] text-emerald-600">{formatUSD(inv.valor_pmenor)}</p>
        </Card>
        )}
        <Card className="p-3 border shadow-sm border-amber-200 bg-amber-50/50">
          <div className="flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4 text-amber-600" /><span className="text-[10px] text-amber-700 uppercase tracking-wide">Sin Stock / Bajo</span></div>
          <p className="heading-font text-lg font-bold text-amber-800">{inv.sin_stock || 0} / {inv.stock_bajo || 0}</p>
        </Card>
        {userRole === "admin" && (
        <>
        <Card className="p-3 border shadow-sm border-blue-200 bg-blue-50/50">
          <div className="flex items-center gap-2 mb-1"><ArrowDownToLine className="h-4 w-4 text-blue-600" /><span className="text-[10px] text-blue-700 uppercase tracking-wide">Ingresos</span></div>
          <p className="heading-font text-lg font-bold text-blue-800">{(ing.total_ingresos || 0).toLocaleString()}</p>
          <p className="mono-font text-[10px] text-blue-600">{(ing.total_unidades_ingreso || 0).toLocaleString()} uds</p>
        </Card>
        <Card className="p-3 border shadow-sm border-violet-200 bg-violet-50/50">
          <div className="flex items-center gap-2 mb-1"><ShoppingBag className="h-4 w-4 text-violet-600" /><span className="text-[10px] text-violet-700 uppercase tracking-wide">Ventas</span></div>
          <p className="heading-font text-lg font-bold text-violet-800">{(ven.total_ventas || 0).toLocaleString()}</p>
          <p className="mono-font text-[10px] text-violet-600">{(ven.total_unidades_venta || 0).toLocaleString()} uds</p>
        </Card>
        <Card className="p-3 border shadow-sm border-[#FF9900]/30 bg-muted/50">
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="h-4 w-4 text-[#FF9900]" /><span className="text-[10px] text-[#FF9900] uppercase tracking-wide">Facturado</span></div>
          <p className="heading-font text-sm font-bold text-[#FF9900]">{formatBs(ven.total_facturado)}</p>
          <p className="mono-font text-[10px] text-[#FF9900]">{formatUSD(ven.total_facturado)}</p>
        </Card>
        </>
        )}
      </div>

      {/* Main View Toggle - Dashboard only for admin */}
      <div className="flex items-center gap-2" data-testid="local-view-toggle">
        <Button variant={mainView === "datos" ? "default" : "outline"} size="sm" onClick={() => setMainView("datos")}
          className="text-xs gap-1.5" data-testid="local-view-datos">
          <TableIcon className="h-3.5 w-3.5" /> Datos
        </Button>
        {userRole === "admin" && (
        <Button variant={mainView === "dashboard" ? "default" : "outline"} size="sm" onClick={() => setMainView("dashboard")}
          className="text-xs gap-1.5" data-testid="local-view-dashboard">
          <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
        </Button>
        )}
      </div>

      {/* ====== DASHBOARD VIEW ====== */}
      {mainView === "dashboard" && (
        <div className="space-y-4" data-testid="local-dashboard-view">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Familias */}
            <Card className="border border-border shadow-sm" data-testid="local-chart-familias">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <div>
                  <h3 className="heading-font text-sm font-semibold text-foreground">Top {famTopFam === "__all__" ? "Familias" : "SubFamilias"} por Valor</h3>
                  <p className="text-[10px] text-muted-foreground">{famTopFam === "__all__" ? "Distribucion por Familia" : `SubFamilias de ${famTopFam}`}</p>
                </div>
                <Select value={famTopFam} onValueChange={setFamTopFam}>
                  <SelectTrigger className="w-[140px] h-7 text-xs" data-testid="dash-fam-top"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">General</SelectItem>
                    {allFamilias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-3">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={topFamilias} layout="vertical" margin={{ left: 5, right: 15 }}>
                    <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="familia" width={110} tick={{ fontSize: 8 }}
                      tickFormatter={v => v.length > 18 ? v.substring(0, 16) + '..' : v} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (<div className="bg-card border rounded-lg shadow-lg p-2.5 text-xs">
                        <p className="font-bold text-foreground">{d.familia}</p>
                        <p className="text-[#FF9900]">{formatBs(d.valor)} ({formatUSD(d.valor)})</p>
                        <p className="text-muted-foreground">{d.items} productos, {d.stock} uds</p>
                      </div>);
                    }} />
                    <Bar dataKey="valor" fill="#0284c7" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-2 p-2.5 bg-muted rounded-lg border border-border" data-testid="info-familias">
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    <span className="font-semibold">Valor de inventario por familia.</span> Cada barra
                    <span className="inline-block w-2.5 h-2.5 rounded-sm mx-1" style={{background:'#0284c7',verticalAlign:'middle'}} />
                    representa el capital inmovilizado (Stock x Precio Menor) de esa familia. Las familias con mayor valor requieren mayor atencion en rotacion.
                  </p>
                </div>
              </div>
            </Card>

            {/* Stock Distribution */}
            <Card className="border border-border shadow-sm" data-testid="local-chart-stock">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <h3 className="heading-font text-sm font-semibold text-foreground">Distribucion de Stock</h3>
                <Select value={famStock} onValueChange={setFamStock}>
                  <SelectTrigger className="w-[140px] h-7 text-xs" data-testid="dash-fam-stock"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">General</SelectItem>
                    {allFamilias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-3">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={stockDistribution} cx="50%" cy="50%" innerRadius={55} outerRadius={95}
                      dataKey="value" nameKey="name" paddingAngle={2}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                      {stockDistribution.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 p-2.5 bg-muted rounded-lg border border-border" data-testid="info-stock">
                  <p className="text-[10px] text-muted-foreground leading-relaxed mb-1.5"><span className="font-semibold">Estado de stock de todos los productos del local.</span></p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    <span className="text-[10px] flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-600 inline-block" /> <b>Rojo - Sin Stock:</b> Productos agotados (0 unidades)</span>
                    <span className="text-[10px] flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500 inline-block" /> <b>Amarillo - Bajo:</b> Stock critico (1-9 unidades)</span>
                    <span className="text-[10px] flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#FF9900] inline-block" /> <b>Azul - Normal:</b> Stock adecuado (10-49 unidades)</span>
                    <span className="text-[10px] flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-600 inline-block" /> <b>Verde - Alto:</b> Stock abundante (50+ unidades)</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Ventas por Familia */}
            <Card className="border border-border shadow-sm" data-testid="local-chart-ventas-fam">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <div>
                  <h3 className="heading-font text-sm font-semibold text-foreground">Ventas por {famVentasFam === "__all__" ? "Familia" : "SubFamilia"}</h3>
                  <p className="text-[10px] text-muted-foreground">{famVentasFam === "__all__" ? "Distribucion del facturado" : `SubFamilias de ${famVentasFam}`}</p>
                </div>
                <Select value={famVentasFam} onValueChange={setFamVentasFam}>
                  <SelectTrigger className="w-[140px] h-7 text-xs" data-testid="dash-fam-ventas-fam"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">General</SelectItem>
                    {ventasFamilias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-3">
                {ventasPorFamilia.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={ventasPorFamilia} cx="50%" cy="50%" innerRadius={45} outerRadius={85}
                          dataKey="total" nameKey="familia" paddingAngle={1}
                          label={({ familia, percent }) => percent > 0.05 ? familia.substring(0, 12) : ''} labelLine={false}>
                          {ventasPorFamilia.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => [formatBs(v), 'Facturado']} />
                        <Legend wrapperStyle={{ fontSize: '9px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-2 p-2.5 bg-muted rounded-lg border border-border" data-testid="info-ventas-fam">
                      <p className="text-[10px] text-muted-foreground leading-relaxed">
                        <span className="font-semibold">Distribucion del facturado por familia.</span> Cada segmento representa el monto total vendido (SubTotal acumulado) de esa familia segun el sistema Mastersoft. Las Top 10 familias con mayor facturacion se muestran.
                      </p>
                    </div>
                  </>
                ) : <p className="text-center text-xs text-muted-foreground py-12">Sin datos de ventas</p>}
              </div>
            </Card>

            {/* Ventas por Mes */}
            <Card className="border border-border shadow-sm" data-testid="local-chart-ventas-mes">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <h3 className="heading-font text-sm font-semibold text-foreground">Facturado por Periodo</h3>
                <Select value={famVentasMes} onValueChange={setFamVentasMes}>
                  <SelectTrigger className="w-[140px] h-7 text-xs" data-testid="dash-fam-ventas-mes"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">General</SelectItem>
                    {ventasFamilias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-3">
                {ventasPorMes.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={ventasPorMes} margin={{ left: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return (<div className="bg-card border rounded-lg shadow-lg p-2.5 text-xs">
                            <p className="font-bold text-foreground">{label}</p>
                            <p className="text-violet-700">Facturado: {formatBs(payload[0]?.value)}</p>
                            <p className="text-violet-600">{formatUSD(payload[0]?.value)}</p>
                          </div>);
                        }} />
                        <Line type="monotone" dataKey="total" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 4, fill: '#7c3aed' }} />
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="mt-2 p-2.5 bg-muted rounded-lg border border-border" data-testid="info-ventas-mes">
                      <p className="text-[10px] text-muted-foreground leading-relaxed">
                        <span className="font-semibold">Evolucion cronologica del facturado mensual.</span> La linea
                        <span className="inline-block w-2.5 h-2.5 rounded-sm mx-1" style={{background:'#7c3aed',verticalAlign:'middle'}} />
                        <b>violeta</b> muestra el monto total facturado (suma de SubTotales) por mes segun las ventas registradas en Mastersoft. El eje X esta ordenado cronologicamente (AAAA-MM). Permite identificar meses con mayor o menor actividad comercial.
                      </p>
                    </div>
                  </>
                ) : <p className="text-center text-xs text-muted-foreground py-12">Sin datos de ventas</p>}
              </div>
            </Card>
          </div>

          {/* Ingresos por Mes */}
          <Card className="border border-border shadow-sm" data-testid="local-chart-ingresos-mes">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <h3 className="heading-font text-sm font-semibold text-foreground">Ingresos QMS por Periodo</h3>
              <Select value={famIngresosMes} onValueChange={setFamIngresosMes}>
                <SelectTrigger className="w-[140px] h-7 text-xs" data-testid="dash-fam-ingresos-mes"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">General</SelectItem>
                  {ingresosFamilias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="p-3">
              {ingresosPorMes.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={ingresosPorMes} margin={{ left: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 9 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (<div className="bg-card border rounded-lg shadow-lg p-2.5 text-xs">
                          <p className="font-bold text-foreground">{label}</p>
                          <p className="text-blue-700">Unidades ingresadas: {payload[0]?.value?.toLocaleString()}</p>
                          <p className="text-emerald-700">Valor de ingreso: {formatBs(payload[1]?.value)}</p>
                          <p className="text-emerald-600">{formatUSD(payload[1]?.value)}</p>
                        </div>);
                      }} />
                      <Bar yAxisId="left" dataKey="cantidad" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Cantidad" />
                      <Bar yAxisId="right" dataKey="valor" fill="#10b981" radius={[4, 4, 0, 0]} name="Valor" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-2 p-2.5 bg-muted rounded-lg border border-border" data-testid="info-ingresos-mes">
                    <p className="text-[10px] text-muted-foreground leading-relaxed mb-1.5">
                      <span className="font-semibold">Ingresos desde Deposito Quintanilla, ordenados cronologicamente (AAAA-MM).</span>
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <span className="text-[10px] flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block" /> <b>Azul - Cantidad:</b> Total de unidades recibidas en el periodo (eje izquierdo)</span>
                      <span className="text-[10px] flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> <b>Verde - Valor:</b> Valor monetario de los ingresos: Cantidad x Precio Mayor (eje derecho, en Bs)</span>
                    </div>
                  </div>
                </>
              ) : <p className="text-center text-xs text-muted-foreground py-12">Sin datos de ingresos</p>}
            </div>
          </Card>
        </div>
      )}

      {/* ====== DATOS VIEW ====== */}
      {mainView === "datos" && (
        <Card className="border border-border shadow-sm" data-testid="local-datos-view">
          {/* Sub-tabs + Actions */}
          <div className="flex items-center justify-between px-4 pt-3 border-b border-border">
            <div className="flex gap-1" data-testid="local-data-tabs">
              {dataTabs.map(tab => (
                <button key={tab.key} onClick={() => { setActiveTab(tab.key); setSearch(''); setSortConfig({ key: null, direction: 'asc' }); resetFilters(); }}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                    activeTab === tab.key ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`} data-testid={`local-dtab-${tab.key}`}>
                  <tab.icon className="h-3.5 w-3.5" /> {tab.label}
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{(data[tab.key] || []).length}</Badge>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 pb-2 flex-wrap">
              {userRole !== "viewer" && (
              <LocalExportReport localId={localId} dataType={activeTab} count={filtered.length}
                filters={{ familia: filterFamilia, subfamilia: filterSubFamilia, loteMin: filterLoteMin, loteMax: filterLoteMax, stockMin: filterStockMin, stockMax: filterStockMax }}
                searchTerm={search}
                onOpenExplorer={() => setShowReportsExplorer(true)} />
              )}
              <Button variant="outline" size="sm" onClick={() => setShowFilters(p => !p)} className="text-xs gap-1 h-7" data-testid="local-filter-toggle">
                <Filter className="h-3 w-3" /> Filtros
              </Button>
              {userRole !== "viewer" && (
              <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="text-xs gap-1 h-7" data-testid="local-export-btn">
                {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3" />}
                Exportar Excel
              </Button>
              )}
              <span className="text-[11px] text-muted-foreground"><BarChart3 className="h-3.5 w-3.5 inline mr-1" />{filtered.length} de {currentData.length}</span>
            </div>
          </div>

          {/* Filters Row */}
          {showFilters && (
            <div className="px-4 py-3 bg-muted/80 border-b border-border grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="local-filters">
              <div>
                <label className="text-[10px] text-muted-foreground uppercase mb-1 block">Familia</label>
                <Select value={filterFamilia} onValueChange={v => { setFilterFamilia(v); setFilterSubFamilia("__all__"); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filter-familia"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas</SelectItem>
                    {familias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase mb-1 block">SubFamilia</label>
                <Select value={filterSubFamilia} onValueChange={setFilterSubFamilia}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filter-subfamilia"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas</SelectItem>
                    {subfamilias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {activeTab === "inventario" && <>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-1 block">Stock Min</label>
                  <Input type="number" min="0" value={filterStockMin} onChange={e => setFilterStockMin(parseInt(e.target.value) || 0)}
                    className="h-8 text-xs" data-testid="filter-stock-min" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-1 block">Stock Max</label>
                  <Input type="number" min="0" value={filterStockMax} onChange={e => setFilterStockMax(parseInt(e.target.value) || 999999)}
                    className="h-8 text-xs" data-testid="filter-stock-max" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-1 block">Lote Desde</label>
                  <Select value={filterLoteMin} onValueChange={setFilterLoteMin}>
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-lote-min"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {lotes.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-1 block">Lote Hasta</label>
                  <Select value={filterLoteMax} onValueChange={setFilterLoteMax}>
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-lote-max"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {lotes.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>}
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs h-8 text-muted-foreground" data-testid="filter-reset">Limpiar</Button>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar por codigo o descripcion..."
                className="pl-10 text-xs h-8" data-testid="local-data-search" />
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 min-h-0 overflow-auto max-h-[calc(100vh-460px)]">
            <Table>
              <TableHeader className="sticky top-0 bg-muted/95 backdrop-blur z-10">
                <TableRow>
                  {cols.map(col => (
                    <TableHead key={col.key}>
                      <Button variant="ghost" size="sm" onClick={() => handleSort(col.key)} className="text-[11px] px-1 h-7 font-semibold">
                        {col.label} <ArrowUpDown className="ml-1 h-2.5 w-2.5" />
                      </Button>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow><TableCell colSpan={cols.length} className="text-center py-10 text-xs text-muted-foreground">
                    {currentData.length === 0 ? 'Sin datos. Presiona "Sincronizar" para importar.' : 'Sin resultados'}
                  </TableCell></TableRow>
                ) : sorted.map((row, i) => (
                  <TableRow key={i} className="hover:bg-muted" data-testid={`local-drow-${i}`}>
                    {cols.map(col => (
                      <TableCell key={col.key} className="mono-font text-xs">{renderCell(row, col)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <LocalReportsExplorer open={showReportsExplorer} onOpenChange={setShowReportsExplorer}
        localId={localId} localName={localName} />
    </div>
  );
};

export default LocalStorePage;
